import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Sprout, Loader2 } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";

const HERO = "https://images.unsplash.com/photo-1638807341859-b96fb9bbbb9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxzZWVkbGluZyUyMHBsYW50JTIwZ3Jvd3RoJTIwc3Byb3V0JTIwZ3JlZW4lMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MDc2NTQyMHww&ixlib=rb-4.1.0&q=85";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Seedling" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/90 via-emerald-900/30 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Sprout className="h-6 w-6" />
            </div>
            <span className="font-display font-bold text-2xl">Sprout Log</span>
          </div>
          <h2 className="font-display text-3xl font-bold max-w-md leading-tight">
            Watch every seed become a story.
          </h2>
          <p className="mt-3 text-white/80 max-w-sm">
            Track growth stages, log heights and photos, and never miss a watering again.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm animate-rise">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-xl bg-emerald-700 flex items-center justify-center">
              <Sprout className="h-6 w-6 text-white" />
            </div>
            <span className="font-display font-bold text-2xl">Sprout Log</span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground mt-1.5 mb-8">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to tend your garden.">
      <GoogleButton label="Sign in with Google" />
      <form onSubmit={submit} className="space-y-4 mt-4" data-testid="login-form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@garden.com" required data-testid="login-email-input" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs text-emerald-700 dark:text-emerald-400 font-medium hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password-input" />
        </div>
        {error && (
          <p className="text-sm text-destructive" data-testid="login-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={loading} className="w-full bg-emerald-700 hover:bg-emerald-800 py-2.5" data-testid="login-submit-button">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Sign in
        </Button>
      </form>
      <p className="text-sm text-muted-foreground mt-6 text-center">
        New here?{" "}
        <Link to="/register" className="text-emerald-700 dark:text-emerald-400 font-semibold hover:underline" data-testid="go-register-link">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
